import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Building2, ChevronDown } from 'lucide-react';
import { useAuthContext } from '../../hooks/useAuthContext';
import { InfiniteCanvas } from './InfiniteCanvas';
import { StrategyNodeCard } from './StrategyNodeCard';
import { StrategyConnections } from './StrategyConnections';
import { TimePeriodSelector } from './TimePeriodModal';
import { strategyMapService } from '@/services/strategyMapService';
import { supabase } from '@/lib/supabase-client';
export function StrategyMapCanvas({ userId }) {
    const { userProfile } = useAuthContext();
    const [goals, setGoals] = useState([]);
    const [relationships, setRelationships] = useState([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
    const [organizationName, setOrganizationName] = useState('Organization');
    const [organizations, setOrganizations] = useState([]);
    const [selectedPeriods, setSelectedPeriods] = useState([
        {
            type: 'year',
            year: new Date().getFullYear(),
            period: '',
            startDate: new Date(new Date().getFullYear(), 0, 1),
            endDate: new Date(new Date().getFullYear(), 11, 31)
        }
    ]);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [isTimePeriodModalOpen, setIsTimePeriodModalOpen] = useState(false);
    const [showOrgSelector, setShowOrgSelector] = useState(false);
    const [loading, setLoading] = useState(true);
    const [draggedNode, setDraggedNode] = useState(null);
    const canvasRef = useRef(null);
    // Set organization from user context
    useEffect(() => {
        if (userProfile?.organization_id) {
            setSelectedOrganizationId(userProfile.organization_id);
        }
    }, [userProfile?.organization_id]);
    // Fetch organizations and set the user's organization
    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const { data } = await supabase
                    .from('organizations')
                    .select('id, name')
                    .order('name');
                if (data) {
                    setOrganizations(data);
                    // Find and set the user's organization
                    if (userProfile?.organization_id) {
                        const userOrg = data.find(o => o.id === userProfile.organization_id);
                        if (userOrg) {
                            setSelectedOrganizationId(userOrg.id);
                            setOrganizationName(userOrg.name);
                        }
                    }
                    else if (data.length > 0) {
                        setSelectedOrganizationId(data[0].id);
                        setOrganizationName(data[0].name);
                    }
                }
            }
            catch (error) {
                console.error('Failed to fetch organizations:', error);
            }
        };
        fetchOrganizations();
    }, [userProfile?.organization_id]);
    // Fetch goals and relationships when organization or periods change
    useEffect(() => {
        const fetchData = async () => {
            if (!selectedOrganizationId)
                return;
            setLoading(true);
            try {
                // Merge all selected periods into a single date range
                let periodStart = selectedPeriods[0]?.startDate || new Date();
                let periodEnd = selectedPeriods[0]?.endDate || new Date();
                selectedPeriods.forEach(period => {
                    if (period.startDate < periodStart)
                        periodStart = period.startDate;
                    if (period.endDate > periodEnd)
                        periodEnd = period.endDate;
                });
                const goalsData = await strategyMapService.getStrategyGoals({
                    organizationId: selectedOrganizationId,
                    periodStart,
                    periodEnd
                });
                const relationshipsData = await strategyMapService.getGoalRelationships(selectedOrganizationId);
                setGoals(goalsData);
                setRelationships(relationshipsData);
            }
            catch (error) {
                console.error('Failed to fetch strategy map data:', error);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedOrganizationId, selectedPeriods]);
    // Build node map for rendering
    const nodeMap = useCallback(() => {
        const map = new Map();
        goals.forEach(goal => {
            const childCount = relationships.filter(r => r.parent_goal_id === goal.id).length;
            map.set(goal.id, {
                id: goal.id,
                name: goal.name,
                description: goal.description,
                progress: goal.progress || 0,
                status: goal.status || 'on-track',
                owner: '',
                organization: organizations.find(o => o.id === goal.organization_id)?.name,
                childCount,
                isExpanded: expandedNodes.has(goal.id),
                isSelected: selectedNodeId === goal.id
            });
        });
        return map;
    }, [goals, relationships, expandedNodes, selectedNodeId, organizations]);
    // Build connection data for rendering
    const connectionData = useCallback(() => {
        const nodes = nodeMap();
        const connections = [];
        relationships.forEach(rel => {
            const parent = goals.find(g => g.id === rel.parent_goal_id);
            const child = goals.find(g => g.id === rel.child_goal_id);
            if (parent && child) {
                connections.push({
                    parentId: rel.parent_goal_id,
                    childId: rel.child_goal_id,
                    parentX: parent.canvas_x,
                    parentY: parent.canvas_y,
                    parentW: 288,
                    parentH: 100,
                    childX: child.canvas_x,
                    childY: child.canvas_y,
                    childW: 288,
                    childH: 100,
                    type: rel.relationship_type || 'child',
                    isHighlighted: selectedNodeId === rel.parent_goal_id || selectedNodeId === rel.child_goal_id
                });
            }
        });
        return connections;
    }, [goals, relationships, nodeMap, selectedNodeId]);
    const handleNodeDragStart = (e, nodeId) => {
        const goalIndex = goals.findIndex(g => g.id === nodeId);
        if (goalIndex >= 0) {
            setDraggedNode({
                id: nodeId,
                offsetX: e.clientX - goals[goalIndex].canvas_x,
                offsetY: e.clientY - goals[goalIndex].canvas_y
            });
        }
    };
    const handleCanvasDragOver = (e) => {
        e.preventDefault();
    };
    const handleCanvasDrop = async (e) => {
        if (!draggedNode || !canvasRef.current)
            return;
        const rect = canvasRef.current.getBoundingClientRect();
        const newX = e.clientX - rect.left - draggedNode.offsetX;
        const newY = e.clientY - rect.top - draggedNode.offsetY;
        // Update position in state
        const updatedGoals = goals.map(g => g.id === draggedNode.id ? { ...g, canvas_x: newX, canvas_y: newY } : g);
        setGoals(updatedGoals);
        // Save to database (debounced in real implementation)
        await strategyMapService.updateGoalPosition(draggedNode.id, newX, newY);
        setDraggedNode(null);
    };
    const handleNodeSelect = (nodeId, multiSelect = false) => {
        if (multiSelect) {
            // TODO: Implement multi-select
        }
        else {
            setSelectedNodeId(nodeId);
        }
    };
    const handleToggleExpand = (nodeId) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        }
        else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };
    const handleTimePeriodSelect = (selections) => {
        setSelectedPeriods(selections);
    };
    const nodes = nodeMap();
    const connections = connectionData();
    return (_jsxs("div", { className: "flex flex-col h-full w-full bg-gray-50 dark:bg-gray-900", children: [_jsx("div", { className: "flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4", children: _jsxs("div", { className: "flex items-center justify-between gap-4", children: [_jsxs("div", { className: "relative", children: [_jsxs("button", { onClick: () => setShowOrgSelector(!showOrgSelector), className: "flex items-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors", children: [_jsx(Building2, { className: "w-4 h-4" }), _jsx("span", { className: "font-medium", children: organizations.find(o => o.id === selectedOrganizationId)?.name || 'Select Organization' }), _jsx(ChevronDown, { className: "w-4 h-4" })] }), showOrgSelector && (_jsx("div", { className: "absolute top-full left-0 mt-2 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-50 min-w-48", children: organizations.map(org => (_jsx("button", { onClick: () => {
                                            setSelectedOrganizationId(org.id);
                                            setShowOrgSelector(false);
                                        }, className: `w-full px-4 py-2 text-left transition-colors ${selectedOrganizationId === org.id
                                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`, children: org.name }, org.id))) }))] }), _jsxs("button", { onClick: () => setIsTimePeriodModalOpen(true), className: "px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2", children: [_jsx("span", { className: "font-medium", children: selectedPeriods.length === 1
                                        ? selectedPeriods[0].period
                                            ? `${selectedPeriods[0].period} ${selectedPeriods[0].year}`
                                            : `${selectedPeriods[0].year}`
                                        : `${selectedPeriods.length} Periods` }), _jsx(ChevronDown, { className: "w-4 h-4" })] }), _jsx("div", { className: "flex items-center gap-2 ml-auto", children: _jsxs("button", { className: "flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium", children: [_jsx(Plus, { className: "w-4 h-4" }), "New Goal"] }) })] }) }), _jsx("div", { ref: canvasRef, className: "flex-1 relative overflow-hidden", onDragOver: handleCanvasDragOver, onDrop: handleCanvasDrop, children: loading ? (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" }), _jsx("p", { className: "text-gray-600 dark:text-gray-400", children: "Loading strategy map..." })] }) })) : (_jsxs(InfiniteCanvas, { showControls: true, children: [_jsx(StrategyConnections, { connections: connections, selectedNodeId: selectedNodeId, animated: true }), _jsx("div", { className: "relative", children: goals.map(goal => {
                                const node = nodes.get(goal.id);
                                if (!node)
                                    return null;
                                return (_jsx(StrategyNodeCard, { node: node, canvasX: goal.canvas_x, canvasY: goal.canvas_y, onDragStart: handleNodeDragStart, onSelect: handleNodeSelect, onToggleExpand: handleToggleExpand }, goal.id));
                            }) })] })) }), _jsx(TimePeriodSelector, { isOpen: isTimePeriodModalOpen, onClose: () => setIsTimePeriodModalOpen(false), onSelect: handleTimePeriodSelect, selectedPeriods: selectedPeriods })] }));
}
